import { useState, useEffect, useRef } from 'react';
import { Modal } from 'components/index';
import { Stack } from '@koob/margaret';
import { KoobButton } from '@koob/ui';
import {
  CheckoutHotelRoomSelection,
  CheckoutHotelRoomSelectionList,
  CheckoutHotelRoomSelectionRoom,
  CheckoutHotelRoomSelectionRoomDetails,
  clearOfferedRoomSupplementKeys,
  isSameOfferedRoom,
  selectedRoomSupplementKey,
  shiftSelectedRoomSupplementKeys,
  withBedChoicesForPax,
} from '@koob/checkout';
import { generateRoomKey } from 'utils/roomKeyGenerator';
import { useTranslation } from 'react-i18next';
import {
  getBedChoicePrice,
  getRoomPax,
  getStayFlexibleRequirements,
  resolveSupplementOnBed,
  toOptionalSupplementOptions,
} from '@koob/utils';
import { StayFlexibleRequirements } from '@koob/booking';

/**
 * Hotel Room Selection Modal
 *
 * Wraps CheckoutHotelRoomSelection in a modal for use in TripDesigner and ExperienceDetails.
 * Allows users to select specific rooms for their hotel bookings.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {function} props.onRequestClose - Callback to close the modal
 * @param {function} props.onSave - Callback when rooms are saved (receives selectedRooms, availabilityData)
 * @param {Object} props.hotelData - Hotel availability data with possibilities array
 * @param {number} props.totalRoomNumber - Total number of rooms to select
 * @param {Array} props.initialSelectedRooms - Initially selected rooms
 * @param {string} props.hotelName - Hotel name for display
 * @param {Object} props.filters - Room composition filters and stay window
 *   (e.g., { rooms: roomsComposition, startAt, endAt }). The dates are what the
 *   extra-nights note is computed from; without them it stays silent.
 */
const HotelRoomSelectionModal = ({
  isOpen,
  onRequestClose,
  onSave,
  hotelData,
  totalRoomNumber,
  initialSelectedRooms = [],
  hotelName,
  filters,
}) => {
  const { t } = useTranslation('tripDesigner');
  const [selectedRooms, setSelectedRooms] = useState(initialSelectedRooms);
  const [currentSelectedRoom, setCurrentSelectedRoom] = useState(undefined);
  // The supplements ticked on the room being chosen, and the per-room store the
  // selects read back from. Stubbing these out left the supplement select inert:
  // an early check-in could be ticked and was silently dropped.
  const [selectedSupplements, setSelectedSupplements] = useState([]);
  const [selectedPromotionSupplements, setSelectedPromotionSupplements] =
    useState({});
  const validateRoomRef = useRef(null);
  const shouldSaveAfterValidation = useRef(false);

  // Rooms waiting to be handed back to the selection, see below.
  const [roomsPendingRestore, setRoomsPendingRestore] = useState(null);
  const hasRestoredRooms = useRef(false);

  const isRoomStillOffered = room =>
    hotelData?.possibilities?.some(p => p.name === room.name);

  const hasAvailability = (hotelData?.possibilities?.length ?? 0) > 0;

  // Nothing is unavailable until the availability is actually loaded: the
  // checkout basket fetches it while the modal is already open, and announcing
  // every chosen room as gone in the meantime is simply wrong.
  const unavailableRooms = hasAvailability
    ? (initialSelectedRooms ?? []).filter(room => !isRoomStillOffered(room))
    : [];

  // Rooms already chosen for this stay that the availability still offers. The
  // ones it no longer does are listed apart, as unavailable: they cannot be
  // re-selected, and showing them as validated would let them be confirmed —
  // and priced — from stale data.
  const restorableRooms = (initialSelectedRooms ?? [])
    .filter(isRoomStillOffered)
    .map(room => {
      const offeredRoom = hotelData?.possibilities?.find(
        p => p.name === room.name,
      );
      const baseBed =
        room.bed ?? room.bedChoices?.[0] ?? offeredRoom?.bedChoices?.[0];

      return {
        ...(offeredRoom ?? {}),
        ...room,
        bed: baseBed
          ? {
              ...baseBed,
              ...(room.bed ?? {}),
              totalPriceWithPromo:
                room.bed?.totalPriceWithPromo ?? baseBed.totalPriceWithPromo,
            }
          : undefined,
      };
    });

  // Each opening starts from a clean slate; what was already chosen comes back
  // through the effect just below.
  useEffect(() => {
    if (isOpen) {
      setSelectedRooms([]);
      setCurrentSelectedRoom(undefined);
      setSelectedSupplements([]);
      setSelectedPromotionSupplements({});
      shouldSaveAfterValidation.current = false;
      setRoomsPendingRestore(null);
      hasRestoredRooms.current = false;
    }
  }, [isOpen]);

  // Reopening the modal is an EDIT: it starts from what the stay already holds,
  // ticked, instead of an empty list the user had to fill again from scratch.
  // It waits for the availability — the checkout basket only fetches it once the
  // modal is open, and a room is restored by matching it against what is offered.
  // Once per opening, so a later refetch cannot wipe what is being picked.
  useEffect(() => {
    if (!isOpen || !hasAvailability || hasRestoredRooms.current) {
      return;
    }
    hasRestoredRooms.current = true;
    setRoomsPendingRestore(
      restorableRooms.slice(0, totalRoomNumber || restorableRooms.length),
    );
  }, [isOpen, hasAvailability]);

  // The restore lands one commit later: the selection only (re)builds its list
  // of offered rooms while nothing is selected, so handing the rooms back in the
  // same pass left that list empty.
  useEffect(() => {
    if (!roomsPendingRestore) {
      return;
    }
    setRoomsPendingRestore(null);
    if (roomsPendingRestore.length > 0) {
      setSelectedRooms(roomsPendingRestore);
    }
  }, [roomsPendingRestore]);

  // Every slot is filled — either just now, or by the rooms restored on
  // reopening. `totalRoomNumber` is not always a number (a composition that is
  // not an array), so an empty selection never counts as complete.
  const isSelectionComplete =
    selectedRooms.length > 0 && selectedRooms.length >= totalRoomNumber;

  // Auto-save when all rooms are validated and user clicked Confirm
  useEffect(() => {
    if (
      shouldSaveAfterValidation.current &&
      selectedRooms.length === totalRoomNumber &&
      selectedRooms.length > 0
    ) {
      shouldSaveAfterValidation.current = false;
      handleSave();
    }
  }, [selectedRooms, totalRoomNumber]);

  const handleSave = () => {
    // Generate room keys for all selected rooms
    const roomsWithKeys = selectedRooms.map((room, index) => {
      const baseBed =
        room.bed ??
        room.bedChoices?.[0] ??
        hotelData?.possibilities?.find(p => p.name === room.name)
          ?.bedChoices?.[0];
      const bed = baseBed ? { ...baseBed, ...(room.bed ?? {}) } : undefined;

      return {
        ...room,
        bed,
        roomKey: generateRoomKey(room, index, bed?.id ?? null),
      };
    });

    onSave(roomsWithKeys, hotelData);
    onRequestClose();
  };

  const handleNextOrConfirm = () => {
    // Check if this will be the last room after validation
    const willBeComplete =
      selectedRooms.length === totalRoomNumber ||
      (selectedRooms.length === totalRoomNumber - 1 && currentSelectedRoom);

    // If we have a current selected room and haven't added it yet, validate it first
    if (validateRoomRef.current && currentSelectedRoom) {
      // Mark that we should save after validation completes
      if (willBeComplete) {
        shouldSaveAfterValidation.current = true;
      }
      validateRoomRef.current();
      // The confirmed room keeps its own supplements; the shared selection has
      // to start empty for the next slot, or that slot inherits them — priced
      // in, and warned about as an extra night it never asked for. The ticks the
      // offered card it came from still holds go the same way: keyed by room
      // type, they would come back ticked on the next slot's identical card
      // without ever reaching its price or its payload.
      setSelectedSupplements([]);
      setSelectedPromotionSupplements(clearOfferedRoomSupplementKeys);
    } else if (isSelectionComplete) {
      // All rooms already selected, save immediately
      handleSave();
    }
  };

  // The supplements ticked so far belong to the room they were ticked on. A
  // promotion or bed change stays on that room and keeps them; moving to ANOTHER
  // room must not inherit them — its own select shows nothing ticked, yet the
  // room would be priced with them, warned about as an extra night, and booked
  // with them, and the card left behind would keep them ticked.
  const handleSetCurrentSelectedRoom = nextRoom => {
    if (nextRoom && !isSameOfferedRoom(currentSelectedRoom, nextRoom)) {
      setSelectedSupplements([]);
      setSelectedPromotionSupplements(clearOfferedRoomSupplementKeys);
    }
    setCurrentSelectedRoom(nextRoom);
  };

  const handleValidateRoom = () => {
    // This callback is called when the last room is validated
    // The useEffect watching selectedRooms will handle the save
  };

  const handleDeleteRoom = roomIndex => {
    setSelectedRooms(prev => prev.filter((_, i) => i !== roomIndex));
    setCurrentSelectedRoom(undefined);
    // Same reason as after a validation: whatever the removed room carried must
    // not seed the next selection, and the slots after it move up.
    setSelectedSupplements([]);
    setSelectedPromotionSupplements(prev =>
      clearOfferedRoomSupplementKeys(
        shiftSelectedRoomSupplementKeys(prev, roomIndex),
      ),
    );
  };

  // Supplements stay editable after a room is validated. A validated room is no
  // longer what `currentSelectedRoom` tracks, so the change is written into its
  // stored entry directly; the price moves by the DELTA rather than being
  // recomputed, which keeps it right whatever was ticked before.
  const updateRoomSupplements = (roomIndex, supplements) => {
    setSelectedRooms(previousRooms =>
      previousRooms.map((room, index) => {
        if (index !== roomIndex) {
          return room;
        }
        const sum = list =>
          (list ?? []).reduce((total, { price }) => total + (price ?? 0), 0);
        const delta = sum(supplements) - sum(room.optionalPromos);

        const offeredRoom = hotelData?.possibilities?.find(
          p => p.name === room.name,
        );
        const baseBed =
          room.bed ?? room.bedChoices?.[0] ?? offeredRoom?.bedChoices?.[0];

        const currentTotalPriceWithPromo =
          room.bed?.totalPriceWithPromo ?? baseBed?.totalPriceWithPromo ?? 0;

        return {
          ...room,
          optionalPromos: supplements,
          bed: {
            ...baseBed,
            ...room.bed,
            totalPriceWithPromo: currentTotalPriceWithPromo + delta,
          },
        };
      }),
    );
  };

  // KD-3732 — the bed configuration stays editable after a room is validated,
  // like its supplements: a room suggested for a composition that has since
  // grown has to move to the extra-bed configuration without being removed and
  // picked again. The room is re-priced off the new bed, its supplements re-read
  // off it since their ids and amounts are per bed.
  const updateRoomBed = (roomIndex, newBed) => {
    setSelectedRooms(previousRooms =>
      previousRooms.map((room, index) => {
        if (index !== roomIndex || !newBed || newBed.id === room.bed?.id) {
          return room;
        }
        const bedSupplementOptions = toOptionalSupplementOptions(
          newBed.optionalSupplements,
        );
        const supplements = (room.optionalPromos ?? []).map(supplement =>
          resolveSupplementOnBed(bedSupplementOptions, supplement),
        );
        const supplementsPrice = supplements.reduce(
          (total, { price }) => total + (price ?? 0),
          0,
        );

        return {
          ...room,
          optionalPromos: supplements,
          bed: {
            ...newBed,
            totalPriceWithPromo: getBedChoicePrice(newBed) + supplementsPrice,
          },
        };
      }),
    );
  };

  // KD-3496 — the nights an early check-in / late check-out needs outside the
  // booked window. KOOB does not reserve them, so they are stated while the
  // rooms are still being picked rather than discovered on arrival.
  const stayFlexibleRequirements = getStayFlexibleRequirements(
    [...selectedRooms, ...(currentSelectedRoom ? [currentSelectedRoom] : [])],
    { startAt: filters?.startAt, endAt: filters?.endAt },
  );
  // The rooms of one booking are priced in one currency, so any picked room
  // carries it — it is what a supplement sold as a flat amount is quoted in.
  const stayFlexibleCurrency = (currentSelectedRoom ?? selectedRooms?.[0])?.bed
    ?.currency;

  // Wrapper for setSelectedRooms that handles replacement when totalRoomNumber is 1
  const handleSetSelectedRooms = newRoomsOrUpdater => {
    setSelectedRooms(prev => {
      const newRooms =
        typeof newRoomsOrUpdater === 'function'
          ? newRoomsOrUpdater(prev)
          : newRoomsOrUpdater;

      // Picking a room while every slot is already filled — a single-room stay,
      // or a restored selection being edited — replaces rather than adds: keep
      // the last totalRoomNumber rooms. Guarded, because a composition that is
      // not an array leaves the count undefined and must cap nothing.
      if (totalRoomNumber > 0 && newRooms.length > totalRoomNumber) {
        return newRooms.slice(-totalRoomNumber);
      }

      return newRooms;
    });
  };

  // Button should be disabled when there's no current selected room — unless the
  // selection is already complete (rooms restored on reopening), which is
  // confirmable as it stands.
  const isButtonDisabled = !currentSelectedRoom && !isSelectionComplete;

  // Calculate what the next count will be
  const nextCount =
    currentSelectedRoom && selectedRooms.length < totalRoomNumber
      ? selectedRooms.length + 1
      : selectedRooms.length;

  // Determine button text
  const buttonText =
    nextCount < totalRoomNumber
      ? t('compose.nextRoom', {
          defaultValue: `Next (${nextCount}/${totalRoomNumber})`,
        })
      : t('common.confirm', {
          defaultValue: `Confirm (${totalRoomNumber}/${totalRoomNumber})`,
        });

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      title={t('compose.selectRooms', {
        defaultValue: `Select Rooms - ${hotelName || 'Hotel'}`,
      })}
      size="large"
      footer={
        <Stack gutterSize={1} alignX="flex-end" alignY="center">
          <KoobButton variant="secondary" onClick={onRequestClose}>
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </KoobButton>
          <KoobButton
            variant="primary"
            onClick={handleNextOrConfirm}
            disabled={isButtonDisabled}
            data-testid="confirm-room-selection"
          >
            {buttonText}
          </KoobButton>
        </Stack>
      }
    >
      <div className="p-4">
        {unavailableRooms.length > 0 && (
          <div className="mb-4 space-y-3">
            {unavailableRooms.map((room, index) => (
              <div
                key={index}
                className="relative border border-solid border-k-gray p-5 bg-white opacity-70 rounded-2xl"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-bold text-gray-800">
                        {room.name}
                      </h3>
                    </div>

                    <p className="text-sm text-gray-500 mb-4">
                      {room.description ?? ''}
                    </p>
                  </div>

                  <div className="flex flex-col items-end gap-2">
                    <span className="text-red-600 font-bold px-2 py-1 ">
                      {t('compose.roomNotAvailable', {
                        defaultValue: 'This room is not available',
                      })}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <CheckoutHotelRoomSelection
          data={hotelData}
          isLoading={false}
          selectedRooms={selectedRooms}
          setSelectedRooms={handleSetSelectedRooms}
          currentSelectedRoom={currentSelectedRoom}
          setCurrentSelectedRoom={handleSetCurrentSelectedRoom}
          totalRoomNumber={totalRoomNumber}
          roomsComposition={filters?.rooms}
          onValidateRoom={handleValidateRoom}
          onDeleteRoom={handleDeleteRoom}
        >
          {renderProps => {
            // Capture validateRoom function for auto-validation using ref
            validateRoomRef.current = renderProps.validateRoom;

            const renderSelectedRoom = ({
              room,
              selectedRoomIndex,
              filters,
            }) => {
              // KD-3732 — only the bed configurations that suit this slot's
              // travellers are offered.
              const slotRoom = withBedChoicesForPax(
                room,
                getRoomPax(filters?.rooms?.[selectedRoomIndex]),
              );
              // The card shows the configurations of the promotion variant
              // the room was picked on, when it has one.
              const shownPossibility =
                slotRoom.variants?.find(
                  variant => variant.originalIndex === room.originalIndex,
                ) ?? slotRoom;

              return (
                <CheckoutHotelRoomSelectionRoom
                  key={selectedRoomIndex}
                  scope="to"
                  possibility={slotRoom}
                  selectedPossibilityIndex={room.originalIndex}
                  filters={filters}
                  handleSelect={() => {}}
                  // Only an explicit bed pick re-beds a validated room: the
                  // card and its variant footer call `handleSelect` too, with
                  // an index that is not the room's bed.
                  onChangeBed={bedIndex =>
                    updateRoomBed(
                      selectedRoomIndex,
                      shownPossibility.bedChoices?.[bedIndex],
                    )
                  }
                  isSelected={true}
                  isRoomSelected={true}
                >
                  {roomRenderProps => (
                    <CheckoutHotelRoomSelectionRoomDetails
                      {...roomRenderProps}
                      selectedRooms={renderProps.selectedRooms}
                      theme={{}}
                      translations={{
                        choosePromotion: t('compose.choosePromotion', {
                          defaultValue: 'Choose Promotion',
                        }),
                        chooseOptionalPromotion: t(
                          'compose.chooseOptionalPromotion',
                          {
                            defaultValue: 'Choose Optional Supplement',
                          },
                        ),
                        selectedComposition: t('compose.selectedComposition', {
                          defaultValue: 'Selected Bed Configuration',
                        }),
                        requiredSupplement: t('compose.requiredSupplement', {
                          defaultValue: 'Required',
                        }),
                        selectPromotionsPlaceholder: t(
                          'compose.selectPromotionsPlaceholder',
                          { defaultValue: 'Select promotions' },
                        ),
                        selectSupplementsPlaceholder: t(
                          'compose.selectSupplementsPlaceholder',
                          { defaultValue: 'Select supplements' },
                        ),
                      }}
                      renderPromotionTag={({ key, name }) => (
                        <span
                          key={key}
                          className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs"
                        >
                          {name}
                        </span>
                      )}
                      renderRequiredPromotionTag={({ key, name, label }) => (
                        <span
                          key={key}
                          className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs"
                        >
                          {label}: {name}
                        </span>
                      )}
                      renderUpdateButton={({ onClick }) => (
                        <KoobButton variant="danger" onClick={onClick}>
                          {t('compose.removeRoom', { defaultValue: 'Remove' })}
                        </KoobButton>
                      )}
                      deleteRoom={() =>
                        renderProps.deleteRoom(selectedRoomIndex)
                      }
                      promotions={renderProps.promoList || []}
                      selectedRoomsFromPromotionSelect={
                        renderProps.selectedRoomsFromPromotionSelect
                      }
                      selectedPromotionSupplements={
                        selectedPromotionSupplements
                      }
                      setSelectedPromotionSupplements={
                        setSelectedPromotionSupplements
                      }
                      selectedSupplements={selectedSupplements}
                      setSelectedSupplements={setSelectedSupplements}
                      // Two rooms of the same type share an originalIndex, so
                      // keying the supplement store on it would make them
                      // overwrite each other. A validated room is identified by
                      // its slot instead.
                      supplementKey={selectedRoomSupplementKey(
                        selectedRoomIndex,
                      )}
                      updateRoomSupplements={supplements =>
                        updateRoomSupplements(selectedRoomIndex, supplements)
                      }
                      currentSelectedRoom={renderProps.currentSelectedRoom}
                      setCurrentSelectedRoom={
                        renderProps.setCurrentSelectedRoom
                      }
                    />
                  )}
                </CheckoutHotelRoomSelectionRoom>
              );
            };

            const renderAvailableRoom = ({
              key,
              possibility: offeredPossibility,
              possibilityIndex,
              currentIndex,
              filters,
            }) => {
              // KD-3732 — only the bed configurations that suit the travellers
              // of the slot being filled are offered. Every slot filled, a
              // pick replaces a room whose slot is not known: those that suit
              // any of the slots are (KD-3758).
              const nextSlot =
                renderProps.selectedRooms.length < totalRoomNumber
                  ? filters?.rooms?.[renderProps.selectedRooms.length]
                  : undefined;
              const possibility = withBedChoicesForPax(
                offeredPossibility,
                nextSlot
                  ? getRoomPax(nextSlot)
                  : (filters?.rooms ?? []).map(getRoomPax),
              );

              // A KOOB room is grouped: its promotion combinations are separate
              // possibilities, listed in `originalIndexes`. Picking one moves
              // `currentIndex` onto that possibility, so matching the card's own
              // `originalIndex` alone dropped the selection — and with it the
              // price — as soon as a promotion was chosen.
              const isSelectedRoom =
                possibility.variants?.some(
                  variant => variant.originalIndex === currentIndex,
                ) ||
                possibility.originalIndexes?.includes(currentIndex) ||
                currentIndex === possibility.originalIndex;

              return (
                <CheckoutHotelRoomSelectionRoom
                  key={key}
                  scope="to"
                  possibility={possibility}
                  selectedPossibilityIndex={
                    isSelectedRoom ? currentIndex : undefined
                  }
                  filters={filters}
                  handleSelect={(index, bedIndex) => {
                    const newPossibility =
                      possibility.variants?.find(
                        variant => variant.originalIndex === index,
                      ) ?? possibility;

                    renderProps.handleRoomIndexChange(
                      newPossibility.originalIndex,
                      bedIndex ?? 0,
                      newPossibility,
                    );
                  }}
                  isSelected={isSelectedRoom}
                  isRoomSelected={false}
                  testId={`room-${possibilityIndex}`}
                  selectedRoom={
                    isSelectedRoom ? renderProps.currentSelectedRoom : undefined
                  }
                >
                  {roomRenderProps => (
                    <>
                      <CheckoutHotelRoomSelectionRoomDetails
                        {...roomRenderProps}
                        selectedRooms={renderProps.selectedRooms}
                        theme={{}}
                        translations={{
                          choosePromotion: t('compose.choosePromotion', {
                            defaultValue: 'Choose Promotion',
                          }),
                          chooseOptionalPromotion: t(
                            'compose.chooseOptionalPromotion',
                            {
                              defaultValue: 'Choose Optional Supplement',
                            },
                          ),
                          selectedComposition: t(
                            'compose.selectedComposition',
                            {
                              defaultValue: 'Selected Bed Configuration',
                            },
                          ),
                          requiredSupplement: t('compose.requiredSupplement', {
                            defaultValue: 'Required',
                          }),
                          selectPromotionsPlaceholder: t(
                            'compose.selectPromotionsPlaceholder',
                            { defaultValue: 'Select promotions' },
                          ),
                          selectSupplementsPlaceholder: t(
                            'compose.selectSupplementsPlaceholder',
                            { defaultValue: 'Select supplements' },
                          ),
                        }}
                        renderPromotionTag={({ key, name }) => (
                          <span
                            key={key}
                            className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs"
                          >
                            {name}
                          </span>
                        )}
                        renderRequiredPromotionTag={({ key, name, label }) => (
                          <span
                            key={key}
                            className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs"
                          >
                            {label}: {name}
                          </span>
                        )}
                        promotions={renderProps.promoList || []}
                        selectedRoomsFromPromotionSelect={
                          renderProps.selectedRoomsFromPromotionSelect
                        }
                        selectedPromotionSupplements={
                          selectedPromotionSupplements
                        }
                        setSelectedPromotionSupplements={
                          setSelectedPromotionSupplements
                        }
                        selectedSupplements={selectedSupplements}
                        setSelectedSupplements={setSelectedSupplements}
                        currentSelectedRoom={renderProps.currentSelectedRoom}
                        setCurrentSelectedRoom={
                          renderProps.setCurrentSelectedRoom
                        }
                      />
                    </>
                  )}
                </CheckoutHotelRoomSelectionRoom>
              );
            };

            return (
              <CheckoutHotelRoomSelectionList
                isLoading={renderProps.isLoading}
                selectedRooms={renderProps.selectedRooms}
                availableRooms={renderProps.availableRooms}
                filters={filters}
                currentIndex={renderProps.currentIndex}
                currentPossibilityIndex={renderProps.currentPossibilityIndex}
                setCurrentPossibilityIndex={
                  renderProps.setCurrentPossibilityIndex
                }
                promoList={renderProps.promoList}
                selectedPromotionSupplements={selectedPromotionSupplements}
                setSelectedPromotionSupplements={
                  setSelectedPromotionSupplements
                }
                selectedRoomsFromPromotionSelect={
                  renderProps.selectedRoomsFromPromotionSelect
                }
                renderSelectedRoom={renderSelectedRoom}
                renderAvailableRoom={renderAvailableRoom}
              />
            );
          }}
        </CheckoutHotelRoomSelection>

        <div className="mt-4">
          <StayFlexibleRequirements
            requirements={stayFlexibleRequirements}
            currency={stayFlexibleCurrency}
            translationKey="booking"
            keyPrefix="summary.stayFlexible"
            variant="info"
          />
        </div>
      </div>
    </Modal>
  );
};

export default HotelRoomSelectionModal;
